// Replay of a race: the snapshot stream of race-net.ts, sampled at REPLAY_HZ instead of the server's 15 Hz, kept in one
// binary container and played back through the very code that applies a server's snapshots on a client (applyCold on
// every frame, applyHot interpolated between two). Solo or online, the recording is the same: the recorder samples the
// race the client draws. A replay stays readable when the engine changes, because it holds states, not inputs.
//
//   container   magic 'R' u8 · version u8 · header length u32 · header (UTF-8 JSON) · frames: (length u32 · snapshot)…
//   header      { v, hz, opts, me, results, events } — opts rebuilds the same riders (seed, roster, humans, rules…)
//
// The decoder trusts nothing: every length is checked against the buffer, every frame goes through decodeSnapshot.
import type { RaceEvent, RaceOptions, RaceResult, Snapshot } from './race-types.js';
import type { Race } from './race.js';
import { SnapshotStream, applyCold, applyHot } from './race-net.js';
import { encodeSnapshot, decodeSnapshot } from './race-net-binary.js';

export const REPLAY_MAGIC = 0x52, REPLAY_VERSION = 1;
/** Frames per second of race time kept in a replay: the playback interpolates between two, like an online client. */
export const REPLAY_HZ = 5;
/** Bounds of a sane replay: 6 hours at REPLAY_HZ, and a header that is a race configuration, not a payload. */
export const REPLAY_LIMITS = { frames: 6 * 3600 * REPLAY_HZ, headerBytes: 512 * 1024, events: 20000 } as const;

export interface ReplayHeader {
  v: number; hz: number;
  /** Options the race was built with (the ghost's trace left out): `new Race(opts)` gives the same riders. */
  opts: RaceOptions;
  /** Rider id of the player who recorded, followed by default. */
  me: number;
  results: RaceResult[];
  /** Race events (hits, pickups…) with the race time they happened at, for the playback's feed. */
  events: [number, RaceEvent][];
}
export interface Replay { header: ReplayHeader; frames: Snapshot[] }

/** Records a race while it runs: call `sample` once per frame, `event` for what the race drains. */
export class ReplayRecorder {
  private stream = new SnapshotStream();
  private lastT = -Infinity;
  frames: ArrayBuffer[] = [];
  events: [number, RaceEvent][] = [];
  constructor(public hz = REPLAY_HZ) {}
  /** Takes a snapshot when a period of race time went by since the last one. */
  sample(race: Race): boolean {
    if (race.t - this.lastT < 1 / this.hz - 1e-6 || this.frames.length >= REPLAY_LIMITS.frames) return false;
    this.lastT = race.t;
    this.frames.push(encodeSnapshot(this.stream.next(race)));
    return true;
  }
  event(t: number, e: RaceEvent) { if (this.events.length < REPLAY_LIMITS.events) this.events.push([+t.toFixed(2), e]); }
  /** The container, ready to compress and store. */
  pack(opts: RaceOptions, me: number, results: RaceResult[]): Uint8Array {
    return packReplay({ v: REPLAY_VERSION, hz: this.hz, opts: replayOptions(opts), me, results, events: this.events }, this.frames);
  }
}

/** Race options worth keeping: what rebuilds the field. The ghost's trace is left out (the replay shows the riders). */
export function replayOptions(o: RaceOptions): RaceOptions {
  const rest = { ...o };
  delete rest.ghost;
  return rest;
}

const enc = new TextEncoder(), dec = new TextDecoder('utf-8', { fatal: true });

export function packReplay(header: ReplayHeader, frames: ArrayBuffer[]): Uint8Array {
  const h = enc.encode(JSON.stringify(header));
  const size = 6 + h.length + frames.reduce((a, f) => a + 4 + f.byteLength, 0);
  const out = new Uint8Array(size), view = new DataView(out.buffer);
  let pos = 0;
  out[pos++] = REPLAY_MAGIC; out[pos++] = REPLAY_VERSION;
  view.setUint32(pos, h.length, true); pos += 4;
  out.set(h, pos); pos += h.length;
  for (const f of frames) { view.setUint32(pos, f.byteLength, true); pos += 4; out.set(new Uint8Array(f), pos); pos += f.byteLength; }
  return out;
}

const bad = (why: string) => new Error(`Invalid replay: ${why}`);

export function unpackReplay(data: Uint8Array | ArrayBuffer): Replay {
  const buf = data instanceof Uint8Array ? data : new Uint8Array(data);
  const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  if (buf.length < 6 || buf[0] !== REPLAY_MAGIC) throw bad('not a replay');
  if (buf[1] !== REPLAY_VERSION) throw bad(`version ${buf[1]}`);
  let pos = 2;
  const hl = view.getUint32(pos, true); pos += 4;
  if (hl > REPLAY_LIMITS.headerBytes || pos + hl > buf.length) throw bad('header length');
  let header: ReplayHeader;
  try { header = JSON.parse(dec.decode(buf.subarray(pos, pos + hl))); } catch { throw bad('header'); }
  pos += hl;
  if (!header || typeof header !== 'object' || header.v !== REPLAY_VERSION || !header.opts || typeof header.opts !== 'object') throw bad('header fields');
  if (!(header.hz >= 1 && header.hz <= 30) || !Number.isInteger(header.me) || !Array.isArray(header.results) || !Array.isArray(header.events)) throw bad('header fields');
  if (header.events.length > REPLAY_LIMITS.events) throw bad('events');
  const frames: Snapshot[] = [];
  while (pos < buf.length) {
    if (pos + 4 > buf.length) throw bad('frame length');
    const n = view.getUint32(pos, true); pos += 4;
    if (n === 0 || pos + n > buf.length) throw bad('frame length');
    if (frames.length >= REPLAY_LIMITS.frames) throw bad('too many frames');
    frames.push(decodeSnapshot(buf.subarray(pos, pos + n))); pos += n;
  }
  if (!frames.length) throw bad('no frame');
  for (let i = 1; i < frames.length; i++) if (frames[i].t < frames[i - 1].t) throw bad('frames out of order');
  return { header, frames };
}

/** Race time covered by a replay: from its first frame to its last. */
export const replayDuration = (r: Replay) => r.frames[r.frames.length - 1].t - r.frames[0].t;

/**
 * Drives a race through the frames of a replay. `seek` places it at any race time (the cold states are rebuilt from
 * the last full frame), `advance` moves it forward and returns the events that happened on the way.
 */
export class ReplayPlayer {
  /** Race time shown. */
  t: number;
  /** Index of the last frame whose cold state is applied. */
  private applied = -1;
  constructor(public race: Race, public replay: Replay) {
    this.t = replay.frames[0].t;
    this.seek(this.t);
  }
  get start() { return this.replay.frames[0].t; }
  get end() { return this.replay.frames[this.replay.frames.length - 1].t; }
  get done() { return this.t >= this.end; }

  /** Index of the last frame at or before race time `t` (the first frame when `t` is before everything). */
  private indexAt(t: number): number {
    const F = this.replay.frames;
    let lo = 0, hi = F.length - 1;
    while (lo < hi) { const mid = (lo + hi + 1) >> 1; if (F[mid].t <= t) lo = mid; else hi = mid - 1; }
    return lo;
  }

  seek(t: number) {
    const F = this.replay.frames;
    this.t = Math.min(this.end, Math.max(this.start, t));
    const i = this.indexAt(this.t);
    // forward within the applied run: only the frames crossed since; otherwise back to the last full frame
    let from = this.applied >= 0 && i >= this.applied ? this.applied + 1 : i;
    if (from === i) while (from > 0 && !F[from].f) from--;
    for (let k = from; k <= i; k++) applyCold(this.race, F[k]);
    this.applied = i;
    this.hot(i);
  }

  /** Places the riders between frame `i` and the next, at the shown time. */
  private hot(i: number) {
    const F = this.replay.frames, cur = F[i], nxt = F[i + 1];
    if (nxt && nxt.t > cur.t) applyHot(this.race, nxt, Math.min(1, (this.t - cur.t) / (nxt.t - cur.t)), cur);
    else applyHot(this.race, cur);
    this.race.t = this.t;
    this.race.state = cur.st;
    this.race.elimLap = cur.el;
  }

  /** Moves forward by `dt` seconds of race time; returns the events crossed. */
  advance(dt: number): RaceEvent[] {
    const before = this.t;
    this.seek(this.t + Math.max(0, dt));
    return this.replay.header.events.filter(([at]) => at > before && at <= this.t).map(([, e]) => e);
  }
}
